<!--
  Light/dark toggle button.

  Both icons are always in the DOM, stacked absolutely, with `chrome.css`
  cross-fading and rotating them off `html[data-theme]`. That is deliberately
  CSS-driven rather than a `v-if`: swapping the element would restart the
  transition from scratch and the spring animation would never play.

  Presentational only — the state machine is `composables/useTheme.ts`, and the
  caller wires `theme` in and `toggle` out. The button reports state through
  `aria-pressed` (is dark mode on?) and describes the *action* in its label,
  which is the pairing screen readers handle least ambiguously.
-->

<script setup lang="ts">
import type { Theme } from "../composables/useTheme.ts";

defineProps<{ theme: Theme }>();
defineEmits<{ (e: "toggle"): void }>();
</script>

<template>
  <button
    type="button"
    class="lucent-theme-toggle"
    :aria-pressed="theme === 'dark' ? 'true' : 'false'"
    :aria-label="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
    title="Toggle theme"
    @click="$emit('toggle')"
  >
    <svg
      class="icon-moon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
    <svg
      class="icon-sun"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="3" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="21" />
      <line x1="3" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="21" y2="12" />
      <line x1="5.6" y1="5.6" x2="7" y2="7" />
      <line x1="17" y1="17" x2="18.4" y2="18.4" />
      <line x1="5.6" y1="18.4" x2="7" y2="17" />
      <line x1="17" y1="7" x2="18.4" y2="5.6" />
    </svg>
  </button>
</template>
